import {
  isColumnElement,
  isColumnsElement,
  PATH_TO_WIDTH_PROPERTY,
  getColumnWidth,
  doColumnWidthsAddUpTo100,
  getDifferenceInWidths,
  rewidthIfColumnsNotAddUpTo100
} from "./helpers";
import { breakdanceStoreActions } from "@/store/actions/store-actions";
import { findNode, getNodeIndex, getNodeParent } from "@/lib/tree/tree";
import { rewidthColumnsAndCommitToStore } from "@/lib/columns/rewidthing";
import { changeColumnWidthAndCommitToStore } from "./store";
import { documentStore } from "@/store/store";
import { breakdanceUndo } from "../undo/undo";
import {
  actionDependencyMatchByValues,
  dependencyMatch
} from "@/store/actions/registrations";
import { UnitInputValue, UnitInputValueV } from "@/types/unit";
import { pipe } from "fp-ts/lib/function";

export default function registerColumnActions() {
  breakdanceStoreActions.register.onCreatedElement(
    ({ elementId, transactionKey }) => {
      const element = findNode(documentStore.document.tree, elementId);

      if (element && isColumnElement(element)) {
        breakdanceUndo.transaction(
          () => {
            rewidthColumnsAndCommitToStore(element.id);
          },
          `Edit column size`,
          transactionKey
        );
      }
    }
  );

  breakdanceStoreActions.register.onAfterDeletedElement(
    ({ parentIdOfDeletedElement, transactionKey }) => {
      const parentOfDeletedElement = findNode(
        documentStore.document.tree,
        parentIdOfDeletedElement
      );

      if (parentOfDeletedElement && isColumnsElement(parentOfDeletedElement)) {
        breakdanceUndo.transaction(
          () => rewidthColumnsAndCommitToStore(parentOfDeletedElement),
          `Edit column size`,
          transactionKey
        );
      }
    }
  );

  breakdanceStoreActions.register.onMovedElement(
    ({ droppedOnElementId, previousParentId, transactionKey }) => {
      const droppedOnColumns = findNode(
        documentStore.document.tree,
        droppedOnElementId
      );
      const draggedFromColumns = findNode(
        documentStore.document.tree,
        previousParentId
      );

      if (
        !draggedFromColumns ||
        !droppedOnColumns ||
        breakdanceUndo.isPartOfExistingTransaction
      ) {
        return;
      }

      if (doColumnWidthsAddUpTo100(droppedOnColumns) === false) {
        breakdanceUndo.transaction(
          () => rewidthColumnsAndCommitToStore(droppedOnColumns),

          `Edit column size`,
          transactionKey
        );
      }

      if (doColumnWidthsAddUpTo100(draggedFromColumns) === false) {
        breakdanceUndo.transaction(
          () => rewidthColumnsAndCommitToStore(draggedFromColumns),
          `Edit column size`,
          transactionKey
        );
      }
    }
  );

  breakdanceStoreActions.register.onPropertyChange(
    ({ elementId, path, previousValue, value, transactionKey }) => {
      const tree = documentStore.document.tree;

      const columnThatWasResized = findNode(tree, elementId);

      if (!isColumnElement(columnThatWasResized)) return;

      const indexOfResizedColumn = getNodeIndex(tree, elementId);
      const parent = getNodeParent(tree, elementId);

      if (indexOfResizedColumn === false || parent === false) return;

      if (!isColumnsElement(parent)) return;

      if (
        !dependencyMatch([PATH_TO_WIDTH_PROPERTY], path) &&
        !actionDependencyMatchByValues(
          [PATH_TO_WIDTH_PROPERTY],
          value,
          previousValue,
          path
        )
      ) {
        return;
      }

      breakdanceUndo.transaction(
        () => {
          const newValue = pipe(
            UnitInputValueV.decode(value),
            () => value,
            () => value as UnitInputValue
          );

          const indexOfSiblingToResize =
            indexOfResizedColumn === parent.children.length - 1
              ? indexOfResizedColumn - 1
              : indexOfResizedColumn + 1;

          const columnsElement = parent;
          const siblingToResize =
            columnsElement.children[indexOfSiblingToResize];

          const isOutsideAllowedBoundariesOrNumberIsEmpty =
            newValue &&
            "number" in newValue &&
            (parseFloat(String(newValue.number)) > 100 ||
              parseFloat(String(newValue.number)) < 0);

          const valueIsEmpty = newValue === null || newValue === undefined;

          // happens if clearing the field
          if (isOutsideAllowedBoundariesOrNumberIsEmpty || valueIsEmpty) {
            rewidthColumnsAndCommitToStore(columnsElement);
            return;
          }

          const newSiblingWidth =
            getColumnWidth(siblingToResize) +
            getDifferenceInWidths(previousValue, newValue);

          if (newSiblingWidth < 1) {
            /* in theory, we should be using getOnCanvasColumnMinWidthBasedOnPadding,
      and then when we rewidth, the ignore index isn't actually ignored if the
      user made the column size bigger than what would fit the remaining columns
      if they all had min widths of getOnCanvasColumnMinWidthBasedOnPadding
      but fuck that shit. */

            rewidthColumnsAndCommitToStore(
              columnsElement,
              indexOfResizedColumn
            );
          } else {
            changeColumnWidthAndCommitToStore(
              siblingToResize.id,
              newSiblingWidth
            );

            rewidthIfColumnsNotAddUpTo100(columnsElement);
          }
        },
        `Edit column size`,
        transactionKey
      );
    }
  );
}
